import { Button, Flex, useMatches } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";

import { submitPayment } from "../functions";

import { paths } from "@/config/paths";
import { defaultCurrency } from "@/lib/currency";
import { useCreatePayment } from "@/services/payments/create-payment";
import { usePlansUser } from "@/services/plans/get-user-plan";
import { showCustomNotification } from "@/utils/notifications";

import type { Currency } from "@/types/currency";
import type { PlanName } from "@/types/plans";
import type { User } from "@/types/user";
import type { FC } from "react";

export type SubmitProps = {
  user: User | null | undefined;
  selectedPlan: PlanName | null;
  setSelectedPlan: React.Dispatch<React.SetStateAction<PlanName | null>>;
};

export const Submit: FC<SubmitProps> = ({ user, selectedPlan }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isMobile = useMatches({ base: true, sm: false });
  const { data: plans, isLoading: isPlanLoading } = usePlansUser();

  const redirectTo = searchParams.get("redirectTo");

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  console.log(currency, redirectTo, queryClient, navigate, paths);

  const createPaymentMutation = useCreatePayment({
    mutationConfig: {
      onSuccess: (data) => {
        // If changing to free "starter" plan, avoid navigating away — backend updates plan without checkout
        if (selectedPlan === "starter" && data.url) {
          window.location.href = paths.app.dashboard.getHref();
        }

        if (data.url) {
          window.location.href = data.url;
        }
      },
      onError: () => {
        if (selectedPlan === "starter") {
          window.location.href = paths.app.dashboard.getHref();
        }

        showCustomNotification({
          title: t("payments.errors.generic_error"),
          message: t("payments.errors.generic_error_description"),
          autoClose: 6000,
        });
      },
    },
  });

  const submitPlan = submitPayment(selectedPlan, user, createPaymentMutation, plans, false);

  return (
    <>
      {/* Desktop */}
      {!isMobile && (
        <Flex justify="center" my="md" mih="2.5em">
          <Button
            w="15.875em"
            variant="filled"
            disabled={!selectedPlan || !plans || isPlanLoading || createPaymentMutation.isPending}
            onClick={submitPlan}
            aria-label={t("plans.continue")}
          >
            {t("plans.continue")}
          </Button>
        </Flex>
      )}

      {/* Mobile */}
      {isMobile && (
        <Flex justify="center" mt="md" px="0px" w="100%">
          <Button
            variant="filled"
            fullWidth
            maw="100%"
            disabled={!selectedPlan || !plans || isPlanLoading || createPaymentMutation.isPending}
            onClick={submitPlan}
            aria-label={t("plans.continue")}
          >
            {t("plans.continue")}
          </Button>
        </Flex>
      )}
    </>
  );
};
